/**
 * Coupon / discount-code type definitions
 */
import { Timestamp } from 'firebase/firestore';

export type CouponDiscountType = 'fixed' | 'percentage';

/**
 * Derived (not stored) coupon status used for display
 */
export enum CouponStatus {
  ACTIVE = 'active',
  USED = 'used',
  INACTIVE = 'inactive',
  EXPIRED = 'expired',
}

export const CouponStatusLabels: Record<CouponStatus, string> = {
  [CouponStatus.ACTIVE]: 'Active',
  [CouponStatus.USED]: 'Used',
  [CouponStatus.INACTIVE]: 'Inactive',
  [CouponStatus.EXPIRED]: 'Expired',
};

/**
 * Firestore coupon document. The document ID is the uppercased `code`,
 * so the code is the single identifier used everywhere (no separate couponId).
 */
export interface Coupon {
  id: string; // == uppercased code (Firestore doc ID)
  code: string; // uppercased
  description?: string;
  discountType: CouponDiscountType;
  discountValue: number;
  minOrderValue?: number;
  maxDiscount?: number;
  usageLimit: number;
  usageCount: number;
  isActive: boolean;
  expiresAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Form data for admin create/update operations
 */
export interface CouponFormData {
  code: string;
  description?: string;
  discountType: CouponDiscountType;
  discountValue: number;
  minOrderValue?: number;
  maxDiscount?: number;
  usageLimit: number;
  isActive: boolean;
  expiresAt?: Timestamp | null;
}

/**
 * Result returned by validateCoupon
 */
export interface CouponValidationResult {
  valid: boolean;
  message: string;
  discount: number;
  coupon?: Coupon;
}

/**
 * Minimal coupon fields needed by the cart to recompute the discount
 * client-side against the live subtotal (persisted with the cart).
 */
export interface AppliedCoupon {
  code: string;
  discountType: CouponDiscountType;
  discountValue: number;
  minOrderValue?: number;
  maxDiscount?: number;
}

/**
 * Pure discount math — single source of truth shared by the cart selector,
 * client-side validateCoupon, and the server (functions) validations.
 * Returns the INR discount amount (>= 0) for the given subtotal.
 */
export const computeCouponDiscount = (
  coupon: Pick<AppliedCoupon, 'discountType' | 'discountValue' | 'minOrderValue' | 'maxDiscount'>,
  subtotal: number
): number => {
  if (subtotal <= 0) return 0;
  if (coupon.minOrderValue != null && subtotal < coupon.minOrderValue) return 0;

  let discount: number;
  if (coupon.discountType === 'percentage') {
    discount = Math.round((subtotal * coupon.discountValue) / 100);
    if (coupon.maxDiscount != null) {
      discount = Math.min(discount, coupon.maxDiscount);
    }
  } else {
    discount = coupon.discountValue;
  }

  // Never discount more than the subtotal, never negative
  return Math.max(0, Math.min(discount, subtotal));
};
